import { DOCUMENT } from '@angular/common';
import {
    ApplicationRef,
    ComponentFactoryResolver,
    ComponentRef,
    Inject,
    Injectable,
    Injector,
    Type,
} from '@angular/core';

import { ModalRef } from './modal-ref';
import { ModalComponent } from './modal.component';
import { ModalParams } from './modal.interface';

@Injectable({
    providedIn: 'root',
})
export class ModalService {
    modalComponentRef: ComponentRef<ModalComponent>;
    private modalSize = {
        small: 'small',
        medium: 'medium',
        large: 'large',
        full: 'full',
    };

    constructor(
        private resolver: ComponentFactoryResolver,
        private appRef: ApplicationRef,
        private injector: Injector,
        @Inject(DOCUMENT) private document: any,
    ) {}

    open<T>(componentType: Type<T>, params?: ModalParams) {
        const modalRef: ModalRef<T> = new ModalRef<T>();

        const sub = modalRef.afterClosed.subscribe(() => {
            this.removeModalComponentFromBody();
            this.document.querySelector('body').classList.remove('is-clipped');
            sub.unsubscribe();
        });

        const componentModalFactory = this.resolver.resolveComponentFactory(ModalComponent);

        const injector: Injector = Injector.create({
            providers: [
                {
                    provide: ModalRef,
                    useValue: modalRef,
                },
            ],
        });

        const componentModalRef = componentModalFactory.create(injector);

        this.appRef.attachView(componentModalRef.hostView);

        this.document.body.appendChild(componentModalRef.location.nativeElement);

        const componentFactory = this.resolver.resolveComponentFactory(componentType);

        if (params && params.hasOwnProperty('standAlone')) {
            componentModalRef.instance.standAlone = params.standAlone;
        }

        if (params && params.hasOwnProperty('closeActive')) {
            componentModalRef.instance.closeActive = params.closeActive;
        }

        if (params && params.hasOwnProperty('backdropClickActive')) {
            componentModalRef.instance.backdropClickActive = params.backdropClickActive;
        }

        componentModalRef.instance.insertionComp.clear();
        componentModalRef.instance.modalSize = params ? `is-${this.modalSize[params.modalSize] || 'medium'}` : '';

        modalRef.setComponentRef(componentModalRef.instance.insertionComp.createComponent(componentFactory));

        this.modalComponentRef = componentModalRef;

        this.document.querySelector('body').classList.add('is-clipped');

        return modalRef;
    }

    private removeModalComponentFromBody() {
        this.appRef.detachView(this.modalComponentRef.hostView);
        this.modalComponentRef.destroy();
    }
}
